// lib/leasing/terminal-commands.ts
//
// COMMANDS THAT ARE THE WHOLE TURN.
//
// The runner already refuses to re-run an IDENTICAL script (LAST_SCRIPT_SIG_BY_SESSION), which is
// what stops a literal loop. It does not stop the agent from doing the same thing again in slightly
// different words - and on 2026-08-13 that is exactly what happened: asked to "save these params as
// esq-standard", it saved them, narrated it, then narrated and saved them a second time. Two
// identical writes, two paragraphs, one request.
//
// Until now "terminal" existed only as a sentence in a command's description and in
// actionRegistry.ts ("This is a terminal command..."). An instruction is a hope. This list is the
// runtime boundary: a script containing one of these does not get another pass, whatever the agent
// set autoRecall to.
//
// WHAT BELONGS HERE: a command that fully satisfies the request it answers, where a second pass can
// only repeat it or wander. Changing a language, saving a parameter set, loading one. NOT a command
// that is a step in something larger - fetching data, scoring, refining a shortlist - because those
// legitimately continue.
export const TERMINAL_ACTION_TYPES: ReadonlySet<string> = new Set([
  'SET_LANGUAGE',
  'SAVE_LAUNCH_PARAMS',
  'LOAD_LAUNCH_PARAMS',
  // Exporting hands the user a file of the list AS THEY APPROVED IT. A second pass can only
  // re-export it or start improving the list behind the snapshot they just sent out.
  'EXPORT_SHORTLIST',
  'PROPOSE_SOURCING_BUDGET',
  'BUILD_FACTORY_PERSONA_PANEL',
  'BUILD_FACTORY_OBJECT_PANEL',
]);

/** Does this script end the turn by nature, regardless of what the agent asked for? */
export const scriptIsTerminal = (commands: ReadonlyArray<{ action_type?: unknown }> | undefined): boolean =>
  Boolean(commands?.some((command) => typeof command?.action_type === 'string' && TERMINAL_ACTION_TYPES.has(command.action_type)));

/** Funding proposals end a script before any paid continuation can accompany them. */
export const budgetProposalScriptError = (commands: ReadonlyArray<{ action_type?: unknown }>): string | null => {
  const proposalIndex = commands.findIndex(command => command.action_type === 'PROPOSE_SOURCING_BUDGET');
  if (proposalIndex < 0) return null;
  const fundingActions = new Set(['SET_SOURCING_MANDATE', 'LAUNCH_SOURCING_CAMPAIGN', 'EXTEND_SHORTLIST']);
  if (proposalIndex !== commands.length - 1 || commands.some(command => fundingActions.has(String(command.action_type)))) {
    return 'A credit proposal must end its script and cannot accompany a sourcing launch or mandate. Present the proposal and wait for its approval button.';
  }
  return null;
};
